/**
 * Default heuristics for identifying entity types and fields in a GraphQL schema.
 *
 * These heuristics work across common GraphQL servers (PostGraphile, Hasura, Apollo, etc.)
 * by detecting widely-adopted conventions like the Relay connection pattern.
 *
 * All heuristics can be overridden via the `isEntityType`, `isEntityField`, and
 * `classifyField` options in `GraphQLConversionOptions`.
 *
 * @packageDocumentation
 */

import {
	isScalarType,
	isEnumType,
	isObjectType,
	isListType,
	isNonNullType,
	isNamedType,
	type GraphQLObjectType,
	type GraphQLField,
	type GraphQLOutputType,
	type GraphQLNamedType,
} from 'graphql'

import type { FieldTemplate } from './types'
import type { GraphQLConversionFieldMeta, GraphQLConversionOptions } from './types'
import { buildScalarMap, INTERNAL_SCALARS } from './scalars'
import { toSlug, camelToLabel, toPascalCase } from '../naming'

/**
 * Suffixes that identify synthetic/framework types generated by GraphQL servers.
 * Types ending with these suffixes are typically not entities.
 */
const SYNTHETIC_SUFFIXES = [
	'Connection',
	'Edge',
	'Input',
	'Patch',
	'Payload',
	'Condition',
	'Filter',
	'OrderBy',
	'Aggregate',
	'AggregateResult',
	'AggregateFilter',
	'DeleteResponse',
	'InsertResponse',
	'UpdateResponse',
	'MutationResponse',
]

/**
 * Root operation type names that are never entities.
 */
const ROOT_TYPE_NAMES = new Set(['Query', 'Mutation', 'Subscription'])

/**
 * Default heuristic to determine if a GraphQL object type represents an entity.
 * An entity type becomes a Stonecrop doctype.
 *
 * This heuristic excludes:
 * - Introspection types (`__*`)
 * - Root operation types (`Query`, `Mutation`, `Subscription`)
 * - Types with synthetic suffixes (e.g., `*Connection`, `*Edge`, `*Input`)
 * - Types starting with `Node` interface marker (exact match only)
 *
 * @param typeName - The GraphQL type name
 * @param type - The GraphQL object type definition
 * @returns `true` if this type should become a Stonecrop doctype
 * @public
 */
export function defaultIsEntityType(typeName: string, type: GraphQLObjectType): boolean {
	// Exclude introspection types
	if (typeName.startsWith('__')) {
		return false
	}

	// Exclude root operation types
	if (ROOT_TYPE_NAMES.has(typeName)) {
		return false
	}

	// Exclude the Node interface marker type
	if (typeName === 'Node') {
		return false
	}

	// Exclude types matching synthetic suffixes
	for (const suffix of SYNTHETIC_SUFFIXES) {
		if (typeName.endsWith(suffix)) {
			return false
		}
	}

	// Must have at least one field
	const fields = type.getFields()
	if (Object.keys(fields).length === 0) {
		return false
	}

	return true
}

/**
 * Fields to skip by default on entity types.
 * These are internal to GraphQL servers and don't represent semantic data.
 *
 * Relay's global object identifier is deliberately absent: which field carries it is a
 * declaration, not a name. See {@link relayNodeIdField}.
 */
const SKIP_FIELDS = new Set(['__typename', 'clientMutationId'])

/**
 * The name Relay's Object Identification spec gives its marker interface.
 */
const RELAY_NODE_INTERFACE = 'Node'

/**
 * The field carrying Relay's global object identifier on this type, or `undefined` for a type that
 * declares none.
 *
 * Read off the interface rather than matched against a list of names, because the name is a server
 * setting: PostGraphile exposes it as `nodeIdFieldName`, which is `id` under the un-overridden
 * Amber preset, `nodeId` under Stonecrop's, and whatever a foreign host chose under theirs. A
 * hardcoded name is a snapshot of one of those, and gets it wrong in both directions at once — it
 * emits an opaque identifier as a column (whose every read then fails on a column that does not
 * exist), and drops a real column that happens to share the name.
 *
 * The interface must be Relay's marker and not a domain interface that shares its name, so it has
 * to declare exactly one field, a non-null `ID`, and nothing else — anything carrying domain fields
 * is a different interface, and skipping against it would drop real columns.
 *
 * @internal
 */
function relayNodeIdField(type: GraphQLObjectType): string | undefined {
	for (const iface of type.getInterfaces()) {
		if (iface.name !== RELAY_NODE_INTERFACE) continue

		const declared = Object.values(iface.getFields())
		if (declared.length !== 1) continue

		const { namedType, required, isList } = unwrapType(declared[0].type)
		if (required && !isList && namedType.name === 'ID') return declared[0].name
	}

	return undefined
}

/**
 * Default heuristic to filter fields on entity types.
 * Skips internal fields that don't represent meaningful data.
 *
 * @param fieldName - The GraphQL field name
 * @param _field - The GraphQL field definition (unused in default implementation)
 * @param parentType - The parent entity type, whose interfaces declare its Relay identifier
 * @returns `true` if this field should be included
 * @public
 */
export function defaultIsEntityField(
	fieldName: string,
	_field: GraphQLField<unknown, unknown>,
	parentType: GraphQLObjectType
): boolean {
	if (SKIP_FIELDS.has(fieldName)) return false
	return fieldName !== relayNodeIdField(parentType)
}

/**
 * Unwrap NonNull and List wrappers from a GraphQL type, tracking nullability.
 *
 * @param type - The GraphQL output type
 * @returns The unwrapped named type, whether it's required, and whether it's a list
 * @internal
 */
function unwrapType(type: GraphQLOutputType): {
	namedType: GraphQLNamedType
	required: boolean
	isList: boolean
} {
	let required = false
	let isList = false
	let current: GraphQLOutputType = type

	// Unwrap outer NonNull
	if (isNonNullType(current)) {
		required = true
		current = current.ofType
	}

	// Unwrap List
	if (isListType(current)) {
		isList = true
		current = current.ofType

		// Unwrap inner NonNull (e.g., [Type!])
		if (isNonNullType(current)) {
			current = current.ofType
		}
	}

	// At this point, current should be a named type (scalar, enum, or object)
	if (!isNamedType(current)) {
		throw new Error(`Expected a named GraphQL type, got: ${String(current)}`)
	}
	return { namedType: current, required, isList }
}

/**
 * Check if a GraphQL object type looks like a Relay Connection type.
 * A connection type has an `edges` field returning a list of edge types,
 * where each edge has a `node` field.
 *
 * @param type - The GraphQL object type to check
 * @returns The node type name if this is a connection, or `undefined`
 * @internal
 */
function getConnectionNodeType(type: GraphQLObjectType): string | undefined {
	const fields = type.getFields()

	// Must have an 'edges' field
	const edgesField = fields['edges']
	if (!edgesField) return undefined

	// edges must be a list
	const { namedType: edgesType, isList: edgesIsList } = unwrapType(edgesField.type)
	if (!edgesIsList || !isObjectType(edgesType)) return undefined

	// Each edge must have a 'node' field
	const edgeFields = edgesType.getFields()
	const nodeField = edgeFields['node']
	if (!nodeField) return undefined

	const { namedType: nodeType } = unwrapType(nodeField.type)
	if (!isObjectType(nodeType)) return undefined

	return nodeType.name
}

/**
 * Classify a single GraphQL field into a Stonecrop field definition.
 *
 * Classification rules (in order):
 * 1. Scalar types → look up in merged scalar map
 * 2. Enum types → `Select` with enum values as options
 * 3. Object types that are entities → `Link` with slug as options
 * 4. Object types that are Connections → `Doctype` with node type slug as options
 * 5. List of entity type → `Doctype` with item type slug as options
 * 6. Anything else → `Data` with `_unmapped: true`
 *
 * @param fieldName - The GraphQL field name
 * @param field - The GraphQL field definition
 * @param entityTypes - Set of type names classified as entities
 * @param options - Conversion options (for custom scalars, unmapped meta, etc.)
 * @returns The Stonecrop field definition
 * @public
 */
export function classifyFieldType(
	fieldName: string,
	field: GraphQLField<unknown, unknown>,
	entityTypes: Set<string>,
	options: GraphQLConversionOptions = {}
): GraphQLConversionFieldMeta {
	const { namedType, required, isList } = unwrapType(field.type)
	const scalarMap = buildScalarMap(options.customScalars)

	const base: GraphQLConversionFieldMeta = {
		kind: 'field',
		fieldname: fieldName,
		label: camelToLabel(fieldName),
		component: 'ATextInput',
	}

	if (required) {
		base.required = true
	}

	// 1. Scalar types
	if (isScalarType(namedType)) {
		// Skip internal scalars (e.g., Cursor)
		if (INTERNAL_SCALARS.has(namedType.name)) {
			base._unmapped = true
			if (options.includeUnmappedMeta) {
				base._graphqlType = namedType.name
			}
			return base
		}

		// Special case: ID fields that reference an entity type → Link
		if (namedType.name === 'ID') {
			const candidateTypeName = toPascalCase(fieldName)
			if (entityTypes.has(candidateTypeName)) {
				base.component = 'AFormLink'
				base.doctype = toSlug(candidateTypeName)
				return base
			}
		}

		const template: FieldTemplate | undefined = scalarMap[namedType.name]
		if (template) {
			base.component = template.component
		} else {
			// Unknown scalar — default to Data with unmapped marker
			base._unmapped = true
			if (options.includeUnmappedMeta) {
				base._graphqlType = namedType.name
			}
		}
		return base
	}

	// 2. Enum types → Select
	if (isEnumType(namedType)) {
		base.component = 'ADropdown'
		base.options = namedType.getValues().map(v => v.name)
		return base
	}

	// 3–5. Object types
	if (isObjectType(namedType)) {
		// 3. Direct reference to an entity type → Link
		if (!isList && entityTypes.has(namedType.name)) {
			base.component = 'AFormLink'
			base.doctype = toSlug(namedType.name)
			return base
		}

		// 4. Connection type → link (child table)
		const connectionNodeTypeName = getConnectionNodeType(namedType)
		if (connectionNodeTypeName && entityTypes.has(connectionNodeTypeName)) {
			base.component = 'ATable'
			base._isLink = true
			base.doctype = toSlug(connectionNodeTypeName)
			base.cardinality = 'noneOrMany'
			return base
		}

		// 5. List of entity type → link
		if (isList && entityTypes.has(namedType.name)) {
			base.component = 'ATable'
			base._isLink = true
			base.doctype = toSlug(namedType.name)
			base.cardinality = 'noneOrMany'
			return base
		}

		// Unknown object type — mark as unmapped
		base._unmapped = true
		if (options.includeUnmappedMeta) {
			base._graphqlType = namedType.name
		}
		return base
	}

	// Fallback — shouldn't normally be reached
	base._unmapped = true
	if (options.includeUnmappedMeta) {
		base._graphqlType = namedType.name
	}
	return base
}
